import { c as _c } from "react/compiler-runtime";
import { EntityRepository } from '@record-it-npm/synchro-client';
import { EntityInfoUtils } from '@record-it-npm/synchro-common';
import React, { FunctionComponent, MutableRefObject, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useRefWithCurrentValue } from 'record-it-components/hooks/useRefWithCurrentValue';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { EntityChangedPayload } from 'record-it-time-common/dto/synchronization/socket/EntityChangedPayload';
import { CustomHttpHeader } from 'record-it-time-common/enums/CustomHttpHeader';
import { AppSynchronizationEnvironmentTypes } from '../AppSynchronizationEnvironmentTypes';
import { AppEntityManager } from '../classes/entities/AppEntityManager';
import { config } from '../config';
import { SocketContext, SocketContextValue } from '../context/SocketContext';
import { useEntityManager } from '../hooks/entity/useEntityManager';
import { useAuth } from '../hooks/useAuth';
export const AppSocketContextProvider: FunctionComponent<PropsWithChildren> = React.memo(props => {
  const $ = _c(11);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    children
  } = t0;
  const {
    authenticationData
  } = useAuth();
  const {
    entityManager
  } = useEntityManager();
  const entityManagerRef = useRefWithCurrentValue(entityManager);
  const [socket, setSocket] = useState(null);
  let t1;
  if ($[2] !== socket) {
    t1 = {
      socket
    };
    $[2] = socket;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const contextValue = t1;
  let t2;
  let t3;
  if ($[4] !== authenticationData || $[5] !== entityManagerRef) {
    t2 = () => {
      let createdSocket = null;
      if (authenticationData) {
        createdSocket = createSocket(authenticationData.csrfToken, entityManagerRef);
        setSocket(createdSocket);
      }
      return () => {
        createdSocket?.disconnect();
      };
    };
    t3 = [authenticationData, entityManagerRef];
    $[4] = authenticationData;
    $[5] = entityManagerRef;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== children || $[9] !== contextValue) {
    t4 = <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>;
    $[8] = children;
    $[9] = contextValue;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
});
function createSocket(csrfToken: string, entityManagerRef: MutableRefObject<AppEntityManager>): Socket {
  const socket = io(config.get('SocketMainBaseUrl'), {
    path: '/websocket',
    extraHeaders: {
      [CustomHttpHeader.CsrfToken]: csrfToken
    },
    withCredentials: true
  });
  let hasConnected = false;
  socket.on('connect', () => {
    if (hasConnected) {
      // only start the actualizations on a reconnect
      void entityManagerRef.current.entityActualization.actualize();
    }
    hasConnected = true;
  });
  socket.on('entityChanged', ({
    entity,
    entityName
  }: EntityChangedPayload<any>) => {
    try {
      const repository = entityManagerRef.current.entityRepositoryContainer.getByEntityName(entityName) as EntityRepository<AppSynchronizationEnvironmentTypes, any, any>;
      if (!entity.deleted) {
        repository.addExternalEntityLocally(entity);
      } else {
        EntityInfoUtils.applyDefaultValues(repository.getEntityInfo(), entity);
        repository.removeLocally(entity);
      }
    } catch (e) {
      // ignore repositories which couldn't be found
      // this happens when a new entity is implemented but it doesn't exist on the client yet
      if (e instanceof Error && e.message.startsWith('no repository found for')) {
        // just additonal debugging information if you are testing old clients + new servers
        // eslint-disable-next-line no-console
        console.error(e);
      } else {
        throw e;
      }
    }
  });
  return socket;
}